import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { 
  FaMapMarkerAlt, FaClock, FaUsers, FaDollarSign, FaComments, FaInfoCircle, FaTimes,
  FaSeedling, FaHardHat, FaIndustry, FaHome, FaBolt, FaTruck,
  FaShoppingCart, FaUmbrellaBeach, FaUniversity, FaHospital,
  FaGraduationCap, FaLaptop, FaFilm, FaBriefcase, FaLandmark,
  FaPalette, FaFutbol, FaLeaf, FaPlane, FaHandshake, FaCalendarAlt
} from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { chatService } from '../services/chatService';
import ReportButton from '../components/ReportButton';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  background-color: ${props => props.theme.colors.background};
  min-height: calc(100vh - 200px);
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const VentureCard = styled.div`
  background-color: ${props => props.theme.colors.surface};
  border-radius: 10px;
  padding: 2rem;
  box-shadow: 0 2px 10px ${props => props.theme.colors.shadow};
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    padding: 1.25rem;
  }
`;

const VentureIcon = styled.div`
  width: 80px;
  height: 80px;
  background: ${props => props.$gradient || 'linear-gradient(135deg, #8B5A96, #A569BD)'};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 2rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
    font-size: 1.5rem;
    margin-bottom: 1rem;
  }
`;

const VentureTitle = styled.h1`
  color: ${props => props.theme.colors.text};
  margin-bottom: 1rem;
  font-size: 2rem;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 0.75rem;
  }
`;

const VentureDescription = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 1.1rem;
  margin-bottom: 2rem;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 0.95rem;
    margin-bottom: 1.5rem;
  }
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const DetailItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  background-color: ${props => props.theme.colors.background};
  border-radius: 8px;
  border: 1px solid ${props => props.theme.colors.border};
  font-size: 0.95rem;
  
  svg {
    color: ${props => props.$isGig ? '#FF6B6B' : '#8B5A96'};
    font-size: 1.2rem;
    margin-top: 0.1rem;
    flex-shrink: 0;
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem;
    gap: 0.5rem;
    font-size: 0.85rem;
    
    svg {
      font-size: 1rem;
    }
  }
`;

const DetailContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
  
  strong {
    color: ${props => props.theme.colors.text};
    font-size: 0.85rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 600;
  }
  
  span {
    color: ${props => props.theme.colors.textSecondary};
    font-size: 0.95rem;
  }
  
  @media (max-width: 768px) {
    strong {
      font-size: 0.75rem;
    }
    
    span {
      font-size: 0.85rem;
    }
  }
`;

const FullStorySection = styled.div`
  background-color: ${props => props.theme.colors.background};
  padding: 1.5rem;
  border-radius: 10px;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
    margin-bottom: 1.5rem;
  }
`;

const FullStoryTitle = styled.h3`
  color: ${props => props.theme.colors.text};
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 0.75rem;
  }
`;

const FullStoryText = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    line-height: 1.5;
  }
`;

const ChatButton = styled.button`
  background: ${props => props.$isGig 
    ? 'linear-gradient(135deg, #FF6B6B, #FF8E53)' 
    : 'linear-gradient(135deg, #8B5A96, #A569BD)'};
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 25px;
  font-size: 1.1rem;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: transform 0.3s ease;
  margin-bottom: 2rem;
  
  &:hover {
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem 1.25rem;
    font-size: 0.95rem;
    margin-bottom: 1rem;
    gap: 0.4rem;
  }
`;

const ChatModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0,0,0,0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ChatContainer = styled.div`
  background-color: ${props => props.theme.colors.surface};
  width: 90%;
  max-width: 500px;
  height: 70%;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
`;

const ChatHeader = styled.div`
  background: ${props => props.$isGig 
    ? 'linear-gradient(135deg, #FF6B6B, #FF8E53)' 
    : 'linear-gradient(135deg, #8B5A96, #A569BD)'};
  color: white;
  padding: 1rem;
  border-radius: 10px 10px 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ChatMessages = styled.div`
  flex: 1;
  padding: 1rem;
  overflow-y: auto;
  background-color: ${props => props.theme.colors.background};
`;

const WelcomeMessage = styled.div`
  background-color: #e3f2fd;
  padding: 1rem;
  border-radius: 10px;
  margin-bottom: 1rem;
  color: #1565c0;
  font-style: italic;
`;

const ChatInputContainer = styled.div`
  padding: 1rem;
  display: flex;
  gap: 0.5rem;
  background-color: ${props => props.theme.colors.surface};
  border-radius: 0 0 10px 10px;
`;

const ChatInput = styled.input`
  flex: 1;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 20px;
  background-color: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text};
  
  &::placeholder {
    color: ${props => props.theme.colors.textSecondary};
  }
`;

const SendButton = styled.button`
  background: ${props => props.$isGig 
    ? 'linear-gradient(135deg, #FF6B6B, #FF8E53)' 
    : 'linear-gradient(135deg, #8B5A96, #A569BD)'};
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 20px;
  cursor: pointer;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
`;

const LoginPrompt = styled.div`
  background-color: #fff3cd;
  color: #856404;
  padding: 1rem;
  border-radius: 10px;
  margin-bottom: 2rem;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 0.75rem;
    margin-bottom: 1rem;
  }
`;

const LoginButton = styled.button`
  background: ${props => props.$isGig 
    ? 'linear-gradient(135deg, #FF6B6B, #FF8E53)' 
    : 'linear-gradient(135deg, #8B5A96, #A569BD)'};
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 20px;
  cursor: pointer;
  margin-left: 1rem;
  
  @media (max-width: 768px) {
    padding: 0.6rem 1rem;
    font-size: 0.9rem;
    margin-left: 0.5rem;
  }
`;

// Industry icon mapping
const getIndustryIcon = (industry) => {
  const iconMap = {
    'Agriculture & Forestry': { icon: FaSeedling, gradient: 'linear-gradient(135deg, #4CAF50, #81C784)' },
    'Mining & Natural Resources': { icon: FaHardHat, gradient: 'linear-gradient(135deg, #795548, #A1887F)' },
    'Manufacturing': { icon: FaIndustry, gradient: 'linear-gradient(135deg, #607D8B, #90A4AE)' },
    'Construction & Real Estate': { icon: FaHome, gradient: 'linear-gradient(135deg, #FF9800, #FFB74D)' },
    'Utilities & Energy': { icon: FaBolt, gradient: 'linear-gradient(135deg, #FFC107, #FFD54F)' },
    'Transportation & Logistics': { icon: FaTruck, gradient: 'linear-gradient(135deg, #3F51B5, #7986CB)' },
    'Retail & Wholesale Trade': { icon: FaShoppingCart, gradient: 'linear-gradient(135deg, #E91E63, #F06292)' },
    'Hospitality & Tourism': { icon: FaUmbrellaBeach, gradient: 'linear-gradient(135deg, #00BCD4, #4DD0E1)' },
    'Financial Services': { icon: FaUniversity, gradient: 'linear-gradient(135deg, #4CAF50, #66BB6A)' },
    'Healthcare & Life Sciences': { icon: FaHospital, gradient: 'linear-gradient(135deg, #F44336, #EF5350)' },
    'Education & Training': { icon: FaGraduationCap, gradient: 'linear-gradient(135deg, #9C27B0, #BA68C8)' },
    'Information & Communication Technology (ICT)': { icon: FaLaptop, gradient: 'linear-gradient(135deg, #2196F3, #64B5F6)' },
    'Technology & IT': { icon: FaLaptop, gradient: 'linear-gradient(135deg, #2196F3, #64B5F6)' },
    'Media & Entertainment': { icon: FaFilm, gradient: 'linear-gradient(135deg, #673AB7, #9575CD)' },
    'Professional & Business Services': { icon: FaBriefcase, gradient: 'linear-gradient(135deg, #455A64, #78909C)' },
    'Public Sector & Non-Profit': { icon: FaLandmark, gradient: 'linear-gradient(135deg, #5D4037, #8D6E63)' },
    'Arts, Culture & Creative Industries': { icon: FaPalette, gradient: 'linear-gradient(135deg, #FF5722, #FF8A65)' },
    'Sports & Recreation': { icon: FaFutbol, gradient: 'linear-gradient(135deg, #8BC34A, #AED581)' },
    'Environmental & Sustainability': { icon: FaLeaf, gradient: 'linear-gradient(135deg, #009688, #4DB6AC)' },
    'Aerospace & Defense': { icon: FaPlane, gradient: 'linear-gradient(135deg, #3F51B5, #5C6BC0)' },
    'Consumer Services': { icon: FaHandshake, gradient: 'linear-gradient(135deg, #FF9800, #FFAB40)' }
  };
  
  // Default icon based on type
  const defaultIcon = { icon: FaUsers, gradient: 'linear-gradient(135deg, #8B5A96, #A569BD)' };
  return iconMap[industry] || defaultIcon;
};

const BusinessDetailsButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.primary};
  cursor: pointer;
  text-decoration: underline;
  margin-top: 0.5rem;
`;

const SkillsSection = styled.div`
  margin-bottom: 1rem;
  
  strong {
    color: ${props => props.theme.colors.text};
  }
`;

const SkillsContainer = styled.div`
  margin-top: 0.5rem;
`;

const SkillTag = styled.span`
  display: inline-block;
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  padding: 0.25rem 0.75rem;
  border-radius: 15px;
  margin: 0.25rem;
  font-size: 0.9rem;
  border: 1px solid ${props => props.theme.colors.border};
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    padding: 0.2rem 0.6rem;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 0.75rem;
  }
`;

const NoSkillsText = styled.span`
  color: ${props => props.theme.colors.textSecondary};
  font-style: italic;
`;

const VentureDetail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const [creating, setCreating] = useState(false);
  
  const listing = location.state?.listing || location.state?.gig || {};
  const { icon: IconComponent, gradient } = getIndustryIcon(listing.industry);
  const isGig = listing.type === 'gig';
  // Override gradient for gigs to use orange gradient
  const displayGradient = isGig ? 'linear-gradient(135deg, #FF6B6B, #FF8E53)' : gradient;

  if (!listing) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <h2>Listing not found</h2>
          <button onClick={() => navigate('/find-hustle')}>
            Back to Find Hustle
          </button>
        </div>
      </Container>
    );
  }

  const handleStartConversation = async () => {
    if (!user || !profile) {
      navigate('/login');
      return;
    }

    if (listing.owner_id === user.id) {
      toast.error('You cannot start a conversation with yourself');
      return;
    }

    setCreating(true);
    
    try {
      // Create or get existing thread
      const thread = await chatService.createThread({
        listingId: listing.id,
        seekerId: user.id,
        entrepreneurId: listing.owner_id
      });

      toast.success('Conversation started!');
      navigate('/messages');
    } catch (error) {
      console.error('Error creating thread:', error);
      toast.error('Failed to start conversation. Please try again.');
    } finally {
      setCreating(false);
    }
  };


  return (
    <Container>
      {!user && (
        <LoginPrompt>
          <FaInfoCircle style={{ marginRight: '0.5rem' }} />
          You can browse ventures for free, but you'll need to create an account to start a conversation with entrepreneurs.
          <LoginButton $isGig={isGig} onClick={() => navigate('/login')}>
            Sign In / Register
          </LoginButton>
        </LoginPrompt>
      )}

      <VentureCard>
        <VentureIcon $gradient={displayGradient}>
          <IconComponent />
        </VentureIcon>
        
        <VentureTitle>{listing.title}</VentureTitle>
        <VentureDescription>{listing.short_description}</VentureDescription>
        
        <DetailsGrid>
          <DetailItem $isGig={isGig}>
            <FaMapMarkerAlt />
            <DetailContent>
              <strong>LOCATION</strong>
              <span>
                {(() => {
                  const loc = listing.location;
                  if (!loc?.province) return 'Not specified';
                  const displayArea = loc.area === 'Other' ? loc.customArea : loc.area;
                  return displayArea ? `${displayArea}, ${loc.province}` : loc.province;
                })()}
              </span>
            </DetailContent>
          </DetailItem>
          
          {!isGig && (
            <>
              <DetailItem $isGig={isGig}>
                <FaClock />
                <DetailContent>
                  <strong>BUSINESS AGE</strong>
                  <span>{listing.age ? `${listing.age} years` : 'Not specified'}</span>
                </DetailContent>
              </DetailItem>
              
              <DetailItem $isGig={isGig}>
                <FaUsers />
                <DetailContent>
                  <strong>TEAM SIZE</strong>
                  <span>{listing.size ? `${listing.size} ${listing.size === '1' ? 'person' : 'people'}` : 'Not specified'}</span>
                </DetailContent>
              </DetailItem>
            </>
          )}
          
          <DetailItem $isGig={isGig}>
            <FaDollarSign />
            <DetailContent>
              <strong>{listing.type === 'gig' ? 'FEE' : 'PAYMENT'}</strong>
              <span>{listing.pay || 'Not specified'}</span>
            </DetailContent>
          </DetailItem>
          
          {!isGig && (
            <>
              <DetailItem $isGig={isGig}>
                <FaIndustry />
                <DetailContent>
                  <strong>INDUSTRY</strong>
                  <span>{listing.industry || 'Not specified'}</span>
                </DetailContent>
              </DetailItem>
              
              <DetailItem $isGig={isGig}>
                <FaBriefcase />
                <DetailContent>
                  <strong>ROLE TYPE</strong>
                  <span>{listing.role || 'Not specified'}</span>
                </DetailContent>
              </DetailItem>
            </>
          )}
          
          <DetailItem $isGig={isGig}>
            <FaClock />
            <DetailContent>
              <strong>TIME COMMITMENT</strong>
              <span>{listing.time_commitment ? `${listing.time_commitment} hours` : 'Not specified'}</span>
            </DetailContent>
          </DetailItem>
          
          <DetailItem $isGig={isGig}>
            <FaHome />
            <DetailContent>
              <strong>WORK STRUCTURE</strong>
              <span>
                {listing.remote 
                  ? listing.remote.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join('-')
                  : 'Not specified'
                }
              </span>
            </DetailContent>
          </DetailItem>
          
          <DetailItem $isGig={isGig}>
            <FaTruck />
            <DetailContent>
              <strong>TRAVELING</strong>
              <span>{listing.traveling ? 'Yes' : 'No'}</span>
            </DetailContent>
          </DetailItem>
          
          {listing.type === 'gig' && listing.filters?.dates && listing.filters.dates.length > 0 && (
            <DetailItem $isGig={isGig} style={{ gridColumn: '1 / -1' }}>
              <FaCalendarAlt />
              <DetailContent>
                <strong>AVAILABLE DATES</strong>
                <span>
                  {listing.filters.dates.map((date, index) => (
                    <div key={date} style={{ marginBottom: index < listing.filters.dates.length - 1 ? '0.25rem' : '0' }}>
                      {new Date(date).toLocaleDateString('en-ZA', { 
                        weekday: 'short',
                        year: 'numeric', 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </div>
                  ))}
                </span>
              </DetailContent>
            </DetailItem>
          )}
        </DetailsGrid>

        <SkillsSection>
          <strong>SKILLS NEEDED:</strong>
          <SkillsContainer>
            {listing.skills?.map(skill => (
              <SkillTag key={skill}>
                {skill.charAt(0).toUpperCase() + skill.slice(1)}
              </SkillTag>
            )) || <NoSkillsText>No skills specified</NoSkillsText>}
          </SkillsContainer>
        </SkillsSection>

        {listing.full_story && (
          <FullStorySection>
            <FullStoryTitle>The Full Story</FullStoryTitle>
            <FullStoryText>{listing.full_story}</FullStoryText>
          </FullStorySection>
        )}

        <ButtonWrapper>
          <ChatButton $isGig={isGig} onClick={handleStartConversation} disabled={creating}>
            <FaComments />
            {creating ? 'Starting Conversation...' : 'Start Conversation'}
          </ChatButton>
          
          <ReportButton 
            subjectType="listing" 
            subjectId={listing.id} 
            subjectTitle={listing.title}
          />
        </ButtonWrapper>
      

      </VentureCard>

     

    </Container>
  );
};

export default VentureDetail;